import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { OnboardingContext } from './OnboardingProvider';
import type { AdvanceOn, GuideStep, PlacementSide } from './types';
import {
  dispatchOnboardingUI,
  regionForTarget,
  subscribeOnboardingEvent,
  type OnboardingInteractionEvent,
  type OnboardingRegion,
} from './uiAdapter';
import { AnimatedHighlight } from './AnimatedHighlight';
import { DemoCursor } from './DemoCursor';

interface DisplayRect { top: number; left: number; width: number; height: number; }

const PAD_DEFAULT = 8;
const RADIUS_DEFAULT = 12;
const TOOLTIP_WIDTH = 360;
const TOOLTIP_GAP = 16;
const OPEN_MENU_SELECTOR = '[role="menu"][data-state="open"]';

function computeRect(el: HTMLElement, pad: number): DisplayRect {
  const r = el.getBoundingClientRect();
  return {
    top: Math.max(0, r.top - pad),
    left: Math.max(0, r.left - pad),
    width: r.width + pad * 2,
    height: r.height + pad * 2,
  };
}

function unionRects(rects: DisplayRect[]): DisplayRect | null {
  if (rects.length === 0) return null;
  let top = Infinity, left = Infinity, right = -Infinity, bottom = -Infinity;
  for (const r of rects) {
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.left + r.width);
    bottom = Math.max(bottom, r.top + r.height);
  }
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * `querySelectorAll` but with our custom `::eq(N)` suffix: `[foo="bar"]::eq(2)`
 * returns just the 3rd match. Without the suffix, returns all matches.
 */
function queryAllEq(selector: string): HTMLElement[] {
  const m = /::eq\((\d+)\)\s*$/.exec(selector);
  if (m) {
    const idx = parseInt(m[1], 10);
    const sel = selector.slice(0, m.index);
    const all = document.querySelectorAll<HTMLElement>(sel);
    return all[idx] ? [all[idx]] : [];
  }
  return Array.from(document.querySelectorAll<HTMLElement>(selector));
}

function computeTooltipPlacement(
  rect: DisplayRect | null,
  preferred: PlacementSide,
  tooltipHeight: number,
  isMobile: boolean,
): { top: number; left: number; side: PlacementSide } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (isMobile || !rect) {
    return { top: vh - tooltipHeight - 20, left: Math.max(12, (vw - Math.min(TOOLTIP_WIDTH, vw - 24)) / 2), side: 'bottom' };
  }
  const w = Math.min(TOOLTIP_WIDTH, vw - 24);
  const sides: PlacementSide[] = preferred === 'auto'
    ? ['bottom', 'top', 'right', 'left']
    : [preferred, 'bottom', 'top', 'right', 'left'];
  for (const side of sides) {
    if (side === 'bottom' && rect.top + rect.height + TOOLTIP_GAP + tooltipHeight < vh) {
      const left = Math.max(12, Math.min(vw - w - 12, rect.left + rect.width / 2 - w / 2));
      return { top: rect.top + rect.height + TOOLTIP_GAP, left, side };
    }
    if (side === 'top' && rect.top - TOOLTIP_GAP - tooltipHeight > 0) {
      const left = Math.max(12, Math.min(vw - w - 12, rect.left + rect.width / 2 - w / 2));
      return { top: rect.top - TOOLTIP_GAP - tooltipHeight, left, side };
    }
    if (side === 'right' && rect.left + rect.width + TOOLTIP_GAP + w < vw) {
      const top = Math.max(12, Math.min(vh - tooltipHeight - 12, rect.top + rect.height / 2 - tooltipHeight / 2));
      return { top, left: rect.left + rect.width + TOOLTIP_GAP, side };
    }
    if (side === 'left' && rect.left - TOOLTIP_GAP - w > 0) {
      const top = Math.max(12, Math.min(vh - tooltipHeight - 12, rect.top + rect.height / 2 - tooltipHeight / 2));
      return { top, left: rect.left - TOOLTIP_GAP - w, side };
    }
  }
  // Fallback: center
  return { top: Math.max(12, (vh - tooltipHeight) / 2), left: Math.max(12, (vw - w) / 2), side: 'auto' };
}

export function GuideOverlay() {
  const ctx = useContext(OnboardingContext);
  const isMobile = useIsMobile();
  // The spotlight can cut holes for multiple targets at once (primary target
  // + every element matched by `additionalHighlight` + any open menus). Each
  // entry renders as a separate hole and a separate highlight ring, so the
  // user sees BOTH anchors in a "drag from A to B" step, not a single huge
  // rectangle spanning everything.
  const [rects, setRects] = useState<DisplayRect[]>([]);
  const [missing, setMissing] = useState(false);
  const [tooltipHeight, setTooltipHeight] = useState(200);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  // Kept for tooltip-placement math — it wants a single bounding box.
  const positionRect = useMemo(() => unionRects(rects), [rects]);

  const step: GuideStep | null = ctx && ctx.isActive && ctx.steps[ctx.currentStepIndex] ? ctx.steps[ctx.currentStepIndex] : null;
  const totalSteps = ctx?.steps.length ?? 0;
  const isActive = !!ctx?.isActive && !!step;

  // Snapshot transient UI state on tour start; restore on tour end.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      wasActiveRef.current = true;
      dispatchOnboardingUI({ type: 'snapshot' });
    } else if (!isActive && wasActiveRef.current) {
      wasActiveRef.current = false;
      dispatchOnboardingUI({ type: 'restore' });
    }
  }, [isActive]);

  // Force-open the region this step's target lives in. On step change, release
  // the previous region (unless the new step needs the same one).
  const forcedRegionRef = useRef<OnboardingRegion>('none');
  useEffect(() => {
    if (!isActive || !step) return;
    const targetAttr = isMobile ? (step.mobileTarget ?? step.target) : step.target;
    const region: OnboardingRegion = step.region ?? regionForTarget(targetAttr, isMobile);
    if (region === forcedRegionRef.current) return;
    if (forcedRegionRef.current !== 'none') {
      dispatchOnboardingUI({ type: 'release-region', region: forcedRegionRef.current });
    }
    forcedRegionRef.current = region;
    if (region !== 'none') {
      dispatchOnboardingUI({ type: 'force-region', region });
    }
  }, [isActive, step, isMobile]);

  // When a step targets a `properties-*` field, make sure something is
  // actually selected — otherwise the panel renders empty and the target
  // can't be found. Fires once per step change.
  useEffect(() => {
    if (!isActive || !step) return;
    const target = step.target;
    if (!target?.startsWith('properties-')) return;
    // Defer so the region force-open happens first.
    const tid = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('fw-onboarding-event', {
        detail: { name: 'ensure:block-selected' },
      }));
    }, 80);
    return () => clearTimeout(tid);
  }, [isActive, step]);

  // When tour ends, also clear the forced region tracker (restore handles UI).
  useEffect(() => {
    if (!isActive) forcedRegionRef.current = 'none';
  }, [isActive]);

  const _measureTarget = useCallback((el: HTMLElement, pad: number) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setRects([computeRect(el, pad)]));
  }, []);

  useLayoutEffect(() => {
    if (!isActive || !step) { setRects([]); setMissing(false); return; }
    if (step.centered) { setRects([]); setMissing(false); return; }
    const targetAttr = isMobile ? (step.mobileTarget ?? step.target) : step.target;
    if (!targetAttr) { setRects([]); setMissing(false); return; }

    let resolved = false;
    let cancelled = false;
    const pad = step.padding ?? PAD_DEFAULT;
    const observers: ResizeObserver[] = [];
    let mo: MutationObserver | null = null;
    let primaryEl: HTMLElement | null = null;

    const resolvePrimary = (): HTMLElement | null => {
      const candidates = [targetAttr, ...(step!.fallbackTargets ?? [])];
      for (const t of candidates) {
        const el = document.querySelector<HTMLElement>(`[data-guide="${t}"]`);
        if (el) return el;
      }
      return null;
    };

    const collectSecondaryElements = (): HTMLElement[] => {
      const els: HTMLElement[] = [];
      if (step!.includeOpenMenus) {
        document.querySelectorAll<HTMLElement>(OPEN_MENU_SELECTOR).forEach(m => els.push(m));
      }
      if (step!.additionalHighlight) {
        queryAllEq(step!.additionalHighlight).forEach(el => els.push(el));
      }
      return els;
    };

    const remeasure = () => {
      if (!primaryEl) return;
      const all: DisplayRect[] = [computeRect(primaryEl, pad)];
      collectSecondaryElements().forEach(el => all.push(computeRect(el, pad)));
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => setRects(all));
    };

    const resolveOnce = () => {
      if (cancelled || resolved) return;
      const el = resolvePrimary();
      if (!el) return;
      resolved = true;
      primaryEl = el;
      setMissing(false);
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); } catch { /* */ }
      remeasure();
      const ro1 = new ResizeObserver(() => remeasure());
      ro1.observe(el); observers.push(ro1);
      const canvasRoot = el.closest<HTMLElement>('[data-canvas-root]');
      if (canvasRoot) {
        const ro2 = new ResizeObserver(() => remeasure());
        ro2.observe(canvasRoot); observers.push(ro2);
      }
    };

    resolveOnce();
    // Keep the MutationObserver active even after primary resolves, so that
    // secondary elements (open menus, dynamically-inserted portals) also
    // trigger a recompute when they appear/disappear.
    mo = new MutationObserver(() => {
      if (!resolved) { resolveOnce(); return; }
      if (step!.includeOpenMenus || step!.additionalHighlight) remeasure();
    });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'data-state'] });

    const timeout = setTimeout(() => { if (!resolved) { setRects([]); setMissing(true); } }, isMobile ? 2500 : 1500);

    const onRefresh = () => {
      if (!primaryEl) {
        primaryEl = resolvePrimary();
      }
      if (primaryEl) remeasure();
    };
    window.addEventListener('resize', onRefresh);
    window.addEventListener('scroll', onRefresh, true);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      observers.forEach(o => o.disconnect());
      mo?.disconnect();
      window.removeEventListener('resize', onRefresh);
      window.removeEventListener('scroll', onRefresh, true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isActive, step, isMobile]);

  useLayoutEffect(() => {
    if (tooltipRef.current) setTooltipHeight(tooltipRef.current.offsetHeight);
  }, [step, rects]);

  const advanceOn = useMemo<AdvanceOn>(() => step?.advanceOn ?? { kind: 'next' }, [step]);
  const isInteractive = advanceOn.kind !== 'next';

  // One-shot "Show me how" demo — separate from the looping `anim`. Pressing
  // the tooltip button flips this on; it resets automatically after the
  // animation duration. Steps without `showMeHow` never display the button.
  const [showMeHowActive, setShowMeHowActive] = useState(false);
  useEffect(() => {
    // Reset whenever the step changes so the demo doesn't persist across steps.
    setShowMeHowActive(false);
  }, [step]);
  useEffect(() => {
    if (!showMeHowActive || !step?.showMeHow) return;
    const tid = setTimeout(() => setShowMeHowActive(false), step.showMeHow.durationMs ?? 3500);
    return () => clearTimeout(tid);
  }, [showMeHowActive, step]);

  // When a native HTML5 drag is in progress anywhere in the document (e.g. the
  // user is dragging a tool from the sidebar towards the canvas), fade the
  // overlay out completely and make absolutely nothing in it pointer-testable.
  // This prevents tooltip buttons or any stacked element from accidentally
  // becoming the dragover/drop target and aborting the drag.
  const [isDraggingDoc, setIsDraggingDoc] = useState(false);
  useEffect(() => {
    if (!isActive) return;
    const start = () => setIsDraggingDoc(true);
    const end = () => setIsDraggingDoc(false);
    window.addEventListener('dragstart', start, true);
    window.addEventListener('dragend', end, true);
    window.addEventListener('drop', end, true);
    return () => {
      window.removeEventListener('dragstart', start, true);
      window.removeEventListener('dragend', end, true);
      window.removeEventListener('drop', end, true);
    };
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !ctx) return;
    const onKey = (e: KeyboardEvent) => {
      // Block canvas shortcuts during tour
      if ((e.ctrlKey || e.metaKey) && ['z', 'y', 'Z', 'Y', 's', 'S'].includes(e.key)) {
        e.stopPropagation(); e.preventDefault(); return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.stopPropagation(); return; }
      if (e.key === 'Escape') { e.preventDefault(); ctx.endTour(); }
      // Enter/Right only auto-advance non-interactive steps; interactive steps
      // must be satisfied (or explicitly skipped via the tooltip button).
      if (!isInteractive && (e.key === 'ArrowRight' || e.key === 'Enter')) {
        e.preventDefault();
        ctx.next();
      }
      if (e.key === 'ArrowLeft') { e.preventDefault(); ctx.prev(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isActive, ctx, isInteractive]);

  // Listen for interaction events dispatched by workflow components and auto
  // advance when they match the current step's `advanceOn` condition.
  useEffect(() => {
    if (!isActive || !ctx || !isInteractive) return;
    const matches = (e: OnboardingInteractionEvent): boolean => {
      switch (advanceOn.kind) {
        case 'click':
          return e.name === 'block:click' && (!advanceOn.target || advanceOn.target === e.target);
        case 'dblclick':
          return e.name === 'block:dblclick' && (!advanceOn.target || advanceOn.target === e.target);
        case 'contextmenu':
          // Accept contextmenu on blocks OR layer cards so the tour advances
          // even when the user's canvas only has layers visible.
          return (e.name === 'block:rightclick' || e.name === 'layer:rightclick' || e.name === 'group:rightclick')
            && (!advanceOn.target || advanceOn.target === e.target);
        case 'menuItemSelected':
          // Advance when a menu item is clicked in any of the custom
          // right-click menus. If `action` is set, only that specific
          // menu item counts (the trigger passes its data-guide as target).
          return (
            e.name === 'block:contextActionSelected' ||
            e.name === 'layer:contextActionSelected' ||
            e.name === 'group:contextActionSelected'
          ) && (!advanceOn.action || advanceOn.action === e.target);
        case 'drop':
          return e.name === 'tool:dropped';
        case 'move':
          return e.name === 'block:moved';
        case 'connectorCreated':
          return e.name === 'connector:created';
        case 'layerEntered':
          return e.name === 'layer:entered';
        case 'groupCreated':
          return e.name === 'group:created';
        case 'event':
          return e.name === advanceOn.name;
        default:
          return false;
      }
    };
    const unsub = subscribeOnboardingEvent((event) => {
      if (matches(event)) {
        // Defer to next tick so the originating interaction's own handlers
        // (e.g. state updates) complete before the tour moves on.
        setTimeout(() => ctx.next(), 40);
      }
    });
    return unsub;
  }, [isActive, ctx, isInteractive, advanceOn]);

  const placement = useMemo(
    () => computeTooltipPlacement(positionRect, step?.placement ?? 'auto', tooltipHeight, isMobile),
    [positionRect, step?.placement, tooltipHeight, isMobile],
  );

  if (!isActive || !step || !ctx) return null;

  const lang = ctx.lang;
  const title = step.title[lang] ?? step.title.en;
  const body = step.body[lang] ?? step.body.en;
  const radius = step.radius ?? RADIUS_DEFAULT;
  const hasHighlight = rects.length > 0 && !step.centered && !missing;

  const tooltipWidth = Math.min(TOOLTIP_WIDTH, window.innerWidth - 24);

  const waitingHintText: string | null = (() => {
    if (!isInteractive) return null;
    if (step.waitingHint) return step.waitingHint[lang] ?? step.waitingHint.en;
    const defaults: Record<AdvanceOn['kind'], { en: string; sv: string }> = {
      next: { en: 'Continue', sv: 'Fortsätt' },
      click: { en: 'Click the highlighted element', sv: 'Klicka på det highlightade elementet' },
      dblclick: { en: 'Double-click the highlighted element', sv: 'Dubbelklicka på det highlightade elementet' },
      contextmenu: { en: 'Right-click the highlighted element', sv: 'Högerklicka på det highlightade elementet' },
      menuItemSelected: { en: 'Pick an option from the menu', sv: 'Välj ett alternativ i menyn' },
      drop: { en: 'Drag a tool onto the canvas', sv: 'Dra ett verktyg till canvasen' },
      move: { en: 'Drag the highlighted block', sv: 'Dra det highlightade blocket' },
      connectorCreated: { en: 'Draw a connector between two blocks', sv: 'Dra en koppling mellan två block' },
      layerEntered: { en: 'Double-click the layer to enter it', sv: 'Dubbelklicka på lagret för att kliva in' },
      groupCreated: { en: 'Group the selected blocks', sv: 'Gruppera de markerade blocken' },
      event: { en: 'Complete the action to continue', sv: 'Utför handlingen för att fortsätta' },
    };
    const d = defaults[advanceOn.kind];
    return (lang === 'sv' ? d.sv : d.en) || d.en;
  })();

  return createPortal(
    <div
      className="fixed inset-0 z-[60] pointer-events-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fw-guide-title"
      aria-describedby="fw-guide-body"
      style={{
        // While a native HTML5 drag is in progress, hide the overlay entirely.
        // `visibility: hidden` removes every element in the portal from hit
        // testing (stacking, pointer-events and drop-target evaluation) so the
        // canvas below receives the drop unimpeded.
        visibility: isDraggingDoc ? 'hidden' : 'visible',
      }}
    >
      <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
        <defs>
          <mask id="fw-guide-spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {hasHighlight && rects.map((r, i) => (
              <rect
                key={i}
                x={r.left}
                y={r.top}
                width={r.width}
                height={r.height}
                rx={radius}
                ry={radius}
                fill="black"
              />
            ))}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(10,15,30,0.72)"
          mask="url(#fw-guide-spotlight-mask)"
          style={{ transition: 'opacity 0.2s ease' }}
        />
      </svg>

      {/* click-outside-to-close (desktop). Disabled on interactive steps so the
          user can actually click the highlighted target. Needs explicit
          pointer-events-auto because the overlay root is now none. */}
      {!isMobile && !isInteractive && (
        <button
          aria-label="Close onboarding"
          className="absolute inset-0 w-full h-full bg-transparent cursor-default pointer-events-auto"
          onClick={ctx.endTour}
          tabIndex={-1}
        />
      )}

      {/* One highlight ring per rect so every spotlight hole is visually
          bordered. Multi-rect is used e.g. in connector-1 (both anchors)
          and in `includeOpenMenus` steps (trigger + open menu content). */}
      {hasHighlight && rects.map((r, i) => (
        <div
          key={i}
          className={cn(
            'absolute pointer-events-none',
            isInteractive && 'animate-pulse',
          )}
          style={{
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
            borderRadius: radius,
            boxShadow: isInteractive
              ? '0 0 0 3px hsl(var(--primary)), 0 0 24px 4px hsl(var(--primary) / 0.5), 0 0 0 9999px rgba(0,0,0,0)'
              : '0 0 0 2px hsl(var(--primary)), 0 0 0 9999px rgba(0,0,0,0)',
            transition: 'all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)',
          }}
        />
      ))}

      {/* Optional animated multi-target highlight (e.g. connector-draw demo) */}
      {step.anim && step.anim.path.length > 0 && (
        <AnimatedHighlight
          path={step.anim.path}
          durationMs={step.anim.durationMs ?? 5000}
          loop={step.anim.loop ?? true}
        />
      )}

      {/* One-shot "Show me how" demo — visible mouse cursor tweens between
          the step selectors with click/rightclick/drag feedback. */}
      {showMeHowActive && step.showMeHow && step.showMeHow.steps.length > 0 && (
        <DemoCursor
          steps={step.showMeHow.steps}
          durationMs={step.showMeHow.durationMs ?? 4500}
          loop={false}
        />
      )}

      <div
        ref={tooltipRef}
        role="group"
        className={cn(
          'absolute bg-background text-foreground border border-border shadow-2xl rounded-2xl p-5',
          // The tooltip wrapper itself is transparent to pointer events so drag
          // operations (e.g. "Drag a tool onto the canvas") can pass through
          // the tooltip area on their way to the canvas. Interactive children
          // below explicitly re-enable pointer events.
          'flex flex-col gap-3 pointer-events-none'
        )}
        style={{
          top: placement.top,
          left: placement.left,
          width: tooltipWidth,
          maxHeight: isMobile ? '55vh' : '70vh',
          overflowY: 'auto',
          transition: 'top 0.3s ease, left 0.3s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {lang === 'sv' ? 'Steg' : 'Step'} {ctx.currentStepIndex + 1} / {totalSteps}
            </div>
            <h3 id="fw-guide-title" className="text-lg font-semibold mt-1">{title}</h3>
          </div>
          <button
            aria-label={lang === 'sv' ? 'Avsluta rundan' : 'End tour'}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted pointer-events-auto"
            onClick={ctx.endTour}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p id="fw-guide-body" className="text-sm text-muted-foreground whitespace-pre-line">{body}</p>

        {missing && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300 border border-amber-200 dark:border-amber-900 rounded-md p-2">
            <span aria-hidden="true" className="mt-0.5">⚠️</span>
            <span>
              {lang === 'sv'
                ? 'Elementet hittades inte. Klicka "Hoppa över detta" nedan eller tryck Esc.'
                : "Couldn't find the element. Click \"Skip this\" below or press Esc."}
            </span>
          </div>
        )}

        {isInteractive && waitingHintText && !missing && (
          <div className="flex items-center gap-2 text-xs font-medium text-primary">
            <span className="inline-flex w-2 h-2 rounded-full bg-primary animate-pulse" aria-hidden="true" />
            <span>{lang === 'sv' ? 'Väntar: ' : 'Waiting: '}{waitingHintText}</span>
          </div>
        )}

        {step.showMeHow && !showMeHowActive && !missing && (
          <button
            className="self-start text-xs font-medium text-primary hover:underline pointer-events-auto"
            onClick={() => setShowMeHowActive(true)}
          >
            {lang === 'sv' ? '▶ Visa mig hur' : '▶ Show me how'}
          </button>
        )}

        <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
          <button
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-40 pointer-events-auto"
            onClick={ctx.prev}
            disabled={ctx.currentStepIndex === 0}
          >
            ← {lang === 'sv' ? 'Tillbaka' : 'Back'}
          </button>
          <div className="flex items-center gap-2">
            <button
              className="text-sm text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-md hover:bg-muted pointer-events-auto"
              onClick={ctx.skipStep}
            >
              {lang === 'sv' ? 'Hoppa över detta' : 'Skip this'}
            </button>
            {!isInteractive && (
              <button
                className="text-sm font-medium bg-primary text-primary-foreground px-4 py-1.5 rounded-md hover:opacity-90 pointer-events-auto"
                onClick={ctx.next}
              >
                {ctx.currentStepIndex === totalSteps - 1
                  ? (lang === 'sv' ? 'Klar' : 'Done')
                  : (lang === 'sv' ? 'Nästa' : 'Next')}
                {' →'}
              </button>
            )}
          </div>
        </div>

        <div aria-live="polite" className="sr-only">
          {`${ctx.currentStepIndex + 1} ${lang === 'sv' ? 'av' : 'of'} ${totalSteps}: ${title}`}
        </div>
      </div>
    </div>,
    document.body
  );
}
